<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import FormVoteInsights from '@/components/FormVoteInsights.vue';
import { useFormsStore, useCommentsStore } from '@/stores';
import type { FormDto, Locales, UserContribution } from '@/services/types/api.types';
import { Constants } from '@/constants';
import { wrapSavingOperation } from '@/helpers/notifications.helper';

const { t, locale } = useI18n();
const formsStore = useFormsStore();
const commentsStore = useCommentsStore();

const props = defineProps<{
  form: FormDto;
  languageEnabled: Locales[];
}>();

const isLoading = ref(false);
const isAnalyzing = ref(false);
const isResetting = ref(false);
const error = ref<string | null>(null);
const selectedAnalysisLanguage = ref<Locales | null>(null);

// Form setup for FormVoteInsights
const { handleSubmit, resetForm } = useForm({
  validationSchema: yup.object({
    ai_insights: yup.mixed().nullable(),
  }),
});

// Analyze and reset handlers triggered from child component
const resolveDefaultLanguage = (): Locales => {
  const interfaceLanguage = locale.value as Locales;
  if (props.languageEnabled.includes(interfaceLanguage)) {
    return interfaceLanguage;
  }
  return props.languageEnabled[0] ?? 'fr';
};

const runAnalyze = async (language?: Locales) => {
  if (!props.form.id) return;
  error.value = null;
  isAnalyzing.value = true;
  try {
    const targetLanguage = language ?? selectedAnalysisLanguage.value ?? resolveDefaultLanguage();
    await formsStore.analyzeForm(props.form.id, targetLanguage);
    const updatedForm = formsStore.getItem(props.form.id);
    if (updatedForm) {
      selectedAnalysisLanguage.value = (updatedForm.ai_insights?.language as Locales | undefined) ?? targetLanguage;
      resetForm({ values: updatedForm });
      await reloadContributions(updatedForm);
    }
  } catch (err) {
    console.error('Error generating insights:', err);
    error.value = t('consultation.ai.error');
  } finally {
    isAnalyzing.value = false;
  }
};

const runReset = async () => {
  if (!props.form.id) return;
  error.value = null;
  isResetting.value = true;
  try {
    await formsStore.resetAnalysis(props.form.id);
    const updatedForm = formsStore.getItem(props.form.id);
    if (updatedForm) {
      resetForm({ values: updatedForm });
      await reloadContributions(updatedForm);
    }
  } catch (err) {
    console.error('Error resetting analysis:', err);
    error.value = t('consultation.ai.resetDialog.error');
  } finally {
    isResetting.value = false;
  }
};

async function reloadContributions(form: FormDto | null) {
  if (!form?.id) return;
  if (form.are_comments_enabled) {
    await commentsStore.fetchCommentsByFormId(form.id);
  } else if (form.type === Constants.Form.Type.OpenQuestion) {
    await formsStore.fetchVotes(form.id);
  }
}

// Fetch data when form changes
watch(
  () => props.form.id,
  async (formId) => {
    if (!formId) return;

    isLoading.value = true;
    try {
      await formsStore.fetchForm(formId);
      const loadedForm = formsStore.getItem(formId);
      if (!loadedForm) return;

      // Fetch comments for forms with comments enabled
      if (loadedForm.are_comments_enabled) {
        await commentsStore.fetchCommentsByFormId(formId);
      }

      // Fetch votes for open questions
      if (loadedForm.type === Constants.Form.Type.OpenQuestion) {
        await formsStore.fetchVotes(formId);
      }

      selectedAnalysisLanguage.value =
        (loadedForm.ai_insights?.language as Locales | undefined) ?? resolveDefaultLanguage();
      resetForm({ values: loadedForm });
    } catch (error) {
      console.error('Error loading form data:', error);
    } finally {
      isLoading.value = false;
    }
  },
  { immediate: true },
);

// Compute contributions based on form type
// Use the latest store version of the form if available
const currentForm = computed<FormDto>(() => formsStore.getItem(props.form.id) ?? props.form);

const contributions = computed<UserContribution[]>(() => {
  if (!currentForm.value?.id) return [];

  const isOpenQuestion = currentForm.value.type === Constants.Form.Type.OpenQuestion;

  // Open questions (AI uses vote.id as answer_id)
  if (isOpenQuestion) {
    const votes = formsStore.getVotes(currentForm.value.id);
    const mapped = votes
      .filter((v) => v.value_text)
      .map((v) => ({
        id: v.id!,
        content: v.value_text!,
        answer_id: v.id!,
        form_response_id: v.form_response_id,
      }));
    return mapped;
  }

  // Comments (AI uses comment.id as answer_id)
  const comments = commentsStore.getCommentsByFormId(currentForm.value.id);
  return comments.map((c) => ({
    id: c.id,
    content: c.comment,
    answer_id: c.id,
  }));
});

// Rebind vee-validate values whenever the store-backed form updates
watch(
  () => currentForm.value,
  (updated) => {
    if (updated && updated.id) {
      if (!updated.ai_insights?.language) {
        selectedAnalysisLanguage.value = resolveDefaultLanguage();
      }
      resetForm({ values: updated });
    }
  },
  { deep: true },
);

watch(
  () => locale.value,
  () => {
    if (!currentForm.value?.ai_insights?.language) {
      selectedAnalysisLanguage.value = resolveDefaultLanguage();
    }
  },
);

// Save handler
const saveFormInsights = handleSubmit(async (values) => {
  wrapSavingOperation(
    async () => await formsStore.saveForm(values as FormDto),
    () => {
      const updatedForm = formsStore.getItem(props.form.id);
      if (updatedForm) {
        resetForm({ values: updatedForm });
      }
    },
  );
});

const updateSelectedAnalysisLanguage = (language: Locales) => {
  selectedAnalysisLanguage.value = language;
};
</script>

<template>
  <div class="form-analysis-container">
    <div v-if="isLoading" class="q-pa-md text-center">
      <q-spinner color="primary" size="50px" />
      <div class="q-mt-md text-grey-7">{{ t('global.loading') }}</div>
    </div>
    <form-vote-insights
      v-else-if="contributions.length > 0"
      :form="currentForm"
      :language-enabled="props.languageEnabled"
      :selected-analysis-language="selectedAnalysisLanguage"
      :contributions="contributions"
      :is-analyzing="isAnalyzing"
      :is-resetting="isResetting"
      :error="error"
      @save="saveFormInsights"
      @update:selected-analysis-language="updateSelectedAnalysisLanguage"
      @analyze="runAnalyze"
      @reset="runReset"
    />
    <q-banner v-else dense class="bg-grey-2 text-grey-8 q-mt-md">
      <template #avatar>
        <q-icon name="info" color="grey-7" />
      </template>
      {{ t('consultation.ai.no_contributions') }}
    </q-banner>
  </div>
</template>

<style scoped lang="scss">
.form-analysis-container {
  width: 100%;
}
</style>
